/**
 * One `DefenderBreakdown`, charted — the board's workhorse panel (L1).
 *
 * Nine breakdowns arrive across the four data panels, and they share every
 * honesty obligation, so they share one component rather than nine near-copies
 * that drift apart:
 *
 * - **Form follows the job (§2).** Severity is part-to-whole over a fixed set of
 *   at most five values, so it is a donut with the total in the hole. Everything
 *   else is a magnitude comparison across categories, so it is a horizontal bar
 *   sorted by value. Nothing here is ever a two-slice donut or a one-bar chart.
 * - **Colour follows meaning (§3).** Severity and sensor health are *status*
 *   scales and wear the reserved tones with an icon beside every label, because
 *   status is never colour-alone. Everything else — Defender product, detection
 *   source, category, MITRE technique, sensor type, issue type — is an
 *   *identity*, so it takes a fixed categorical assignment derived from the
 *   labels sorted alphabetically, which keeps a category's colour stable between
 *   refreshes. `unknown` and `Other` always take the muted token: neither has
 *   anything to attribute.
 * - **The fold is stated, never silent.** A floored breakdown renders its
 *   `DefenderCohortSuppression` as a caption — including when nothing was
 *   folded, because "no cohort was small enough to fold" and "some rows are
 *   missing" are the two readings a silent caption leaves open.
 * - **Truncation makes every count a floor**, and says so beside the numbers
 *   rather than presenting a floor as a census (§8).
 * - **Every chart has a table twin** (`?data=<id>`, §4.4): the exact buckets,
 *   with their counts, shares and flags, one click away and URL-addressable.
 *
 * There is no per-entity drill below this, and there is nothing missing: a
 * bucket is a count of a classification, and the payload holds no identifier to
 * drill into. See `./params.ts`.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Badge, EmptyState, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { ChartCard } from "@/components/board";
import type { DefenderBreakdown, DefenderBucket } from "@/lib/api";
import { workloadTypeConfig } from "@/lib/charts/config";

import {
  bucketFlag,
  bucketShare,
  type BucketStatus,
  bucketTotal,
  EM_DASH,
  formatCount,
  formatPercent,
  MUTED_FILL,
  sensorHealthStatus,
  SEVERITY_ORDER,
  severityStatus,
  suppressionCaption,
  tooltipValue,
  TRUNCATED_NOTE,
} from "./status";

/** Labels that carry no attribution and so never take a categorical colour. */
const UNATTRIBUTED = new Set(["unknown", "Other"]);

/** One charted bucket, with everything the chart, the legend and the grid need. */
interface BreakdownRow {
  /** The upstream label verbatim — the row key and the axis category. */
  key: string;
  /** Display label. Status scales tidy the case; identities pass through. */
  label: string;
  count: number;
  /** Share of the breakdown's total, or `null` when the total is zero. */
  share: number | null;
  color: string;
  /** Status glyph, present only for the status palettes (§3.2). */
  icon: BucketStatus["icon"] | null;
  /** `folded cohort` / `not recognised`, with the note explaining it. */
  flag: { label: string; hint: string } | null;
}

export interface BreakdownPanelProps {
  /** Panel heading. With one series and no legend, this names the series. */
  title: string;
  /** The breakdown to chart. */
  breakdown: DefenderBreakdown;
  /**
   * `donut` for a fixed, small part-to-whole (severity); `bar` for a magnitude
   * ranking. Defaults to `bar`, which is the safe form at any bucket count.
   */
  form?: "donut" | "bar";
  /** What one unit is, for captions: "incidents", "alerts", "sensors". */
  noun: string;
  /** Which colour family the labels belong to. */
  palette: "severity" | "sensorHealth" | "identity";
  /** Chart accessible name — required on every `Chart.Container` (§6). */
  ariaLabel: string;
  /** Board-unique table-twin id from `TWIN_IDS`. */
  twinId: string;
  /** `collected_at` of the panel this breakdown came from. */
  since?: string | null;
  /** Staleness threshold for the `Freshness` dot, in ms. */
  staleAfterMs?: number;
  /** True when the panel's paging stopped at NOVA's cap — counts are floors. */
  truncated: boolean;
  /** Extra caption text, e.g. the window this breakdown covers. */
  caption?: ReactNode;
  /** Heading for the donut hole, e.g. "Open". */
  totalLabel?: string;
  /** What to say when there is nothing to chart — never "0". */
  emptyTitle: string;
  emptyDescription: string;
}

export function BreakdownPanel({
  title,
  breakdown,
  form = "bar",
  noun,
  palette,
  ariaLabel,
  twinId,
  since,
  staleAfterMs,
  truncated,
  caption,
  totalLabel,
  emptyTitle,
  emptyDescription,
}: BreakdownPanelProps) {
  const total = bucketTotal(breakdown.buckets);
  const rows = buildRows(breakdown.buckets, palette, form, total);
  const fold = suppressionCaption(breakdown.suppression, noun);
  const config = Object.fromEntries(
    rows.map((row) => [row.key, { label: row.label, color: row.color }]),
  );

  return (
    <ChartCard<BreakdownRow>
      title={title}
      caption={
        rows.length === 0 ? (
          "Nothing was counted"
        ) : (
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span>
              {formatCount(total)} {noun}
              {truncated ? " or more" : ""}
            </span>
            {caption && <span>· {caption}</span>}
          </span>
        )
      }
      since={since}
      staleAfterMs={staleAfterMs}
      tableTwin={{
        id: twinId,
        ariaLabel: `${title} — every bucket with its count and share`,
        data: rows,
        getRowId: (row) => row.key,
        columns: [
          {
            id: "label",
            header: "Value",
            accessor: (row) => row.label,
            cell: (row) => (
              <span className="flex flex-wrap items-center gap-2">
                {row.icon && <Icon icon={row.icon} size="xs" />}
                <span className="text-fg text-sm">{row.label}</span>
                {row.flag && (
                  <Badge tone="neutral" variant="outline" size="xs">
                    {row.flag.label}
                  </Badge>
                )}
              </span>
            ),
          },
          {
            id: "count",
            header: truncated ? "Count (floor)" : "Count",
            align: "end",
            accessor: (row) => row.count,
            cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
          },
          {
            id: "share",
            header: "Share of what was read",
            align: "end",
            accessor: (row) => row.share,
            cell: (row) => <span className="tabular-nums">{formatPercent(row.share)}</span>,
          },
        ],
      }}
    >
      {rows.length === 0 ? (
        <EmptyState size="sm" title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="flex flex-col gap-3">
          {form === "donut" ? (
            <div className="relative">
              <Chart.Container config={config} className="h-64" aria-label={ariaLabel}>
                <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Chart.Tooltip
                    content={
                      <Chart.TooltipContent
                        // `tooltipValue` first, never `Number(value)`: a series
                        // with no value at the hovered slice arrives as
                        // `undefined`, and `Number(null)` is a fabricated 0.
                        formatter={(value) => `${formatCount(tooltipValue(value))} ${noun}`}
                        hideLabel
                      />
                    }
                  />
                  <Chart.Pie
                    {...Chart.seriesDefaults}
                    data={rows}
                    dataKey="count"
                    nameKey="key"
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="85%"
                    paddingAngle={2}
                    cornerRadius={3}
                    stroke="var(--norican-surface)"
                    strokeWidth={2}
                  >
                    {rows.map((row) => (
                      <Chart.Cell key={row.key} fill={row.color} />
                    ))}
                  </Chart.Pie>
                </Chart.PieChart>
              </Chart.Container>
              {/* The hole carries the total, per the DS donut recipe. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
              >
                <Typography variant="caption" className="font-semibold tracking-wide uppercase">
                  {totalLabel ?? noun}
                </Typography>
                <Typography variant="heading-2" as="span">
                  {formatCount(total)}
                  {truncated ? "+" : ""}
                </Typography>
              </div>
            </div>
          ) : (
            <Chart.Container config={config} className="h-64" aria-label={ariaLabel}>
              <Chart.BarChart
                data={rows}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={180} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: BreakdownRow) => [
                        {
                          label: truncated ? "At least" : "Count",
                          value: formatCount(datum.count),
                        },
                        { label: "Share", value: formatPercent(datum.share) },
                        ...(datum.flag ? [{ label: "Note", value: datum.flag.label }] : []),
                      ]}
                    />
                  }
                />
                <Chart.Bar {...Chart.seriesDefaults} dataKey="count" radius={[0, 3, 3, 0]}>
                  {rows.map((row) => (
                    <Chart.Cell key={row.key} fill={row.color} />
                  ))}
                </Chart.Bar>
              </Chart.BarChart>
            </Chart.Container>
          )}

          {/* A status scale is never colour-alone (§3.2), so the status palettes
              carry an icon legend. Identity palettes name themselves on the
              bar's own category axis and need none. */}
          {palette !== "identity" && (
            <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
              {rows.map((row) => (
                <span key={row.key} className="inline-flex items-center gap-1.5">
                  {row.icon && <Icon icon={row.icon} size="xs" />}
                  {row.label} ({formatCount(row.count)})
                </span>
              ))}
            </div>
          )}

          {rows.some((row) => row.flag != null) && (
            <ul className="text-fg-muted flex flex-col gap-1 text-xs">
              {rows
                .filter((row) => row.flag != null)
                .map((row) => (
                  <li key={row.key}>
                    <span className="text-fg font-medium">{row.label}</span> {EM_DASH}{" "}
                    {row.flag?.hint}
                  </li>
                ))}
            </ul>
          )}

          {fold && (
            <Typography variant="body-sm" tone="muted">
              {fold}
            </Typography>
          )}

          {truncated && (
            <Typography variant="body-sm" tone="muted">
              {TRUNCATED_NOTE}
            </Typography>
          )}
        </div>
      )}
    </ChartCard>
  );
}

/**
 * Project the payload's buckets into chart rows, with their colours and flags.
 *
 * Ordering differs by form on purpose: a donut wears a fixed severity order so a
 * segment does not jump between refreshes, while a ranked bar is sorted by value
 * because that is the comparison it exists to make.
 */
function buildRows(
  buckets: readonly DefenderBucket[],
  palette: BreakdownPanelProps["palette"],
  form: "donut" | "bar",
  total: number,
): BreakdownRow[] {
  // Categorical colours are assigned from the labels sorted alphabetically, not
  // from payload order, so a category keeps its colour when the ranking moves
  // (§3.3 — identity, fixed assignment).
  const identityConfig =
    palette === "identity"
      ? workloadTypeConfig(
          buckets.filter((bucket) => !UNATTRIBUTED.has(bucket.label)).map((bucket) => bucket.label),
        )
      : {};

  const rows = buckets
    .filter((bucket) => bucket.count > 0)
    .map((bucket) => {
      const status = statusFor(bucket.label, palette);
      return {
        key: bucket.label,
        label: status?.label ?? bucket.label,
        count: bucket.count,
        share: bucketShare(bucket.count, total),
        color:
          status?.color ??
          (UNATTRIBUTED.has(bucket.label)
            ? MUTED_FILL
            : (identityConfig[bucket.label]?.color ?? MUTED_FILL)),
        icon: status?.icon ?? null,
        flag: bucketFlag(bucket),
      };
    });

  if (form === "donut" && palette === "severity") {
    return [...rows].sort((a, b) => severityRank(a.key) - severityRank(b.key) || b.count - a.count);
  }
  return [...rows].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** Fixed severity position, with anything unrecognised ranked first (most severe). */
function severityRank(label: string): number {
  const index = SEVERITY_ORDER.indexOf(label.trim().toLowerCase());
  return index === -1 ? -1 : index;
}

/** Status semantics for a label, or `null` for the identity palettes. */
function statusFor(label: string, palette: BreakdownPanelProps["palette"]): BucketStatus | null {
  if (palette === "severity") {
    return severityStatus(label);
  }
  if (palette === "sensorHealth") {
    return sensorHealthStatus(label);
  }
  return null;
}
